// Per-frame drawing state shared by the render modules (background, food, ejected, cells,
// viruses, zone). The renderer fills one `Frame` per draw call; nothing here allocates.

import type { Settings } from '../settings/store.js';
import type { Ctx2D } from './canvas.js';
import type { ViewRect } from './cull.js';
import type { RenderQuality } from './quality.js';
import type { ArenaTheme } from './theme.js';

/** Per-frame draw counters (render/renderer.ts `RenderStats`). */
export interface FrameStats {
  frames: number;
  food: number;
  ejected: number;
  bodies: number;
  nameDraws: number;
  massDraws: number;
  skinDraws: number;
  /** Bodies drawn with jelly offsets. */
  jellyDraws: number;
}

export interface Frame {
  ctx: Ctx2D;
  /** Backing px per CSS px. */
  bs: number;
  /** CSS px per world unit (camera zoom). */
  zoom: number;
  /** Backing px per world unit (`zoom × bs`). */
  scale: number;
  /** World → backing px: `X = x·scale + offX`, `Y = y·scale + offY`. */
  offX: number;
  offY: number;
  /** Backing size in px. */
  width: number;
  height: number;
  /** World rectangle on screen (culling). */
  view: ViewRect;
  /** The map bounds (WELCOME, or the default map). */
  map: ViewRect;
  theme: ArenaTheme;
  quality: RenderQuality;
  settings: Settings;
  reduceMotion: boolean;
  nowMs: number;
  /** Time since the previous drawn frame (ms; 16.7 on the first). */
  dtMs: number;
  boilFrame: number;
  stats: FrameStats;
}

/** Draw in world units from here on. */
export function useWorldTransform(f: Frame): void {
  f.ctx.setTransform(f.scale, 0, 0, f.scale, f.offX, f.offY);
}

/** Draw in backing pixels from here on. */
export function useScreenTransform(f: Frame): void {
  f.ctx.setTransform(1, 0, 0, 1, 0, 0);
}

/** A world x coordinate in backing pixels. */
export function toScreenX(f: Frame, x: number): number {
  return x * f.scale + f.offX;
}

/** A world y coordinate in backing pixels. */
export function toScreenY(f: Frame, y: number): number {
  return y * f.scale + f.offY;
}
